import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type UrlRedirectRule = { id: string; from: string; to: string };

function normalizePathSegment(p: string): string {
  const trimmed = p.trim();
  if (!trimmed) return '/';
  let s = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s;
}

function isExternalUrl(to: string): boolean {
  return /^https?:\/\//i.test(to);
}

function isSafeInternalPath(pathPart: string): boolean {
  const p = pathPart.trim().split('?')[0];
  if (p.startsWith('//')) return false;
  return normalizePathSegment(p).startsWith('/');
}

let rulesCache: UrlRedirectRule[] | null = null;
let rulesPromise: Promise<UrlRedirectRule[]> | null = null;

async function loadRules(): Promise<UrlRedirectRule[]> {
  if (rulesCache) return rulesCache;
  if (!rulesPromise) {
    rulesPromise = fetch('/api/fhxd/url-redirects')
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load URL redirects');
        const json = await res.json();
        if (!json.success || !Array.isArray(json.data?.rules)) return [];
        return json.data.rules as UrlRedirectRule[];
      })
      .then((rules) => {
        rulesCache = rules;
        return rules;
      })
      .catch(() => {
        rulesCache = [];
        return [];
      })
      .finally(() => {
        rulesPromise = null;
      });
  }
  return rulesPromise;
}

/**
 * Loads Salesforce URL_Redirect_Rule__c rows and redirects when the current path
 * matches Redirect_From__c (Redirect_To__c may be internal or absolute http(s)).
 */
function UrlRedirectHandler() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [rules, setRules] = useState<UrlRedirectRule[] | null>(() => rulesCache);

  useEffect(() => {
    loadRules().then(setRules);
  }, []);

  useLayoutEffect(() => {
    if (!rules?.length) return;

    const current = normalizePathSegment(pathname);
    const rule = rules.find((r) => normalizePathSegment(r.from) === current);
    if (!rule?.to?.trim()) return;

    const rawTo = rule.to.trim();
    if (isExternalUrl(rawTo)) {
      window.location.replace(rawTo);
      return;
    }

    if (!isSafeInternalPath(rawTo)) return;

    const q = rawTo.indexOf('?');
    const pathPart = q === -1 ? rawTo : rawTo.slice(0, q);
    const search = q === -1 ? '' : rawTo.slice(q);
    const targetPath = normalizePathSegment(pathPart);
    const fromNorm = normalizePathSegment(rule.from);
    if (fromNorm === targetPath && !search) return;

    navigate(`${targetPath}${search}`, { replace: true });
  }, [pathname, rules, navigate]);

  return null;
}

export default UrlRedirectHandler;
